import { graphqlPost } from "api/graphqlClient";
import { ShowAllButton } from "components/ShowAllButton/ShowAllButton";
import RecordTable from "components/Table/RecordTable";
import { locationColumn } from "components/Table/recordTableUtils";
import {
    DEFAULT_TABLE_DENSITY,
    TableColumn,
    TableDensity,
    getInitialDensity,
    getTableSettings,
    normalizeTableDensity,
} from "components/Table/tableUtils";
import { FeConfigType } from "config/config";
import { getRecordClass, getRecordConfig } from "config/resourceUtils";
import { createdAtDto, createdByDto, modifiedAtDto, modifiedByDto } from "config/utils.jsx";
import { getSchemaFormColumns } from "containers/Resources/screens/ResourcesSingle/AttributeValueSchema";
import { findConfiguredColumn, withObjectTemplateFilter } from "./recordListWidgetSort";
import { canUseSignatureProbe, recordListFetchShape } from "./recordListWidgetSignature";
import {
    RecordDescriptionInlineEdit,
    RecordFieldInlineEditPencil,
    isRecordFieldEditable,
    isRecordFieldEditableWithGitLock,
} from "containers/Resources/screens/ResourcesSingle/RecordFieldInlineEdit";
import { CustomObjectProvisioningState } from "graphql/generated/graphql";
import { isEqual } from "lodash";
import {
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useHistory } from "react-router-dom";
import { RouterContext } from "utils/RouterManager/RouterManager";
import { buildRecordListAdvancedSearchPath } from "../advancedSearchLink";
import { DashboardLiveWidgetShell } from "../DashboardLiveWidgetShell";
import { describeWidgetFetchError } from "../dashboardWidgetError";
import { DashboardRecordLink } from "../DashboardRecordLink";
import { RecordListWidgetConfig, WidgetComponentProps } from "../types";
import { useDashboardLiveRefresh } from "../useDashboardLiveRefresh";
import {
    adaptiveRowHeightForDensity,
    clampAdaptiveDisplayRows,
    clampPageOffset,
    computeAdaptiveRowCount,
    MAX_ADAPTIVE_ROWS,
    measuredAvailableHeight,
    parseRecordListResponse,
} from "./recordListWidgetFit";

// Rows carry whatever the per-type GraphQL selection returns (identity/location
// relations, object_template, …), so keep the row shape permissive.
type ListedRecord = Record<string, any> & {
    id: string;
    name: string;
    created_at?: string;
    provisioning_state?: CustomObjectProvisioningState;
};

const POLL_INTERVAL_MS = 10000;
const DEFAULT_LIMIT = 10;
/** Debounce for the resize-driven page-size refit, in ms. */
const RESIZE_REFIT_DEBOUNCE_MS = 150;
// Row height per density for the adaptive page size: see
// ADAPTIVE_ROW_HEIGHT_PX_BY_DENSITY in recordListWidgetFit.ts. The page size is
// derived from that CONSTANT (not a data-dependent measurement), so the widget
// issues exactly ONE list request per page view: no measure→refetch loop.

/**
 * Fallback height (px) of the server-side pagination footer (`MuiTablePagination`)
 * shown BELOW the rows in adaptive mode, subtracted from the usable height so the
 * footer never pushes the last row past the panel into an internal scrollbar. The
 * live height is measured from the DOM when available (see {@link measureGeometry});
 * this constant is only used before the footer has painted.
 */
const ADAPTIVE_PAGINATION_FOOTER_PX = 52;

/**
 * A cheap, order-sensitive change-signature over a set of listed rows, used to
 * decide whether a `live` re-poll needs the full heavy fetch. `modified_at` bumps
 * on any write to a record, so it is a faithful proxy for "did anything change" at
 * the same granularity as the heavy `isEqual` diff; `created_at` is the fallback
 * for activity/relationship rows (which do not select `modified_at`, exactly as
 * the heavy fetch does), and `provisioning_state` is folded in explicitly so a
 * custom object's live provisioning transition (CREATING → READY …) is detected.
 */
const computeRowsSignature = (rows: Array<Record<string, any>>): string =>
    rows
        .map(
            (r) =>
                `${r.id}:${r.modified_at ?? r.created_at ?? ""}:${
                    r.provisioning_state ?? ""
                }`,
        )
        .join("|");

/**
 * Relation/scalar fields (available on every `<resource>_plain_list`) needed to
 * render the shared default column set — Location, Modified/Created at & by.
 */
const RESOURCE_DEFAULT_FIELDS = `
    comment_count
    created_at
    modified_at
    created_by_identity { id name identity_type is_deleted }
    modified_by_identity { id name identity_type is_deleted }
    project_id_project { id name icon }
    bundle_id_bundle { id name icon }
    workspace_id_workspace { id name }
`;

/**
 * `record_list` widget — a live list of records of a chosen type, optionally filtered.
 *
 * Fetches via the existing `<type>_plain_list` GraphQL query. When `live` is set it
 * re-polls on a short interval so provisioning state stays current. Rows are rendered
 * through the shared {@link RecordTable} component (the same generic table used by
 * advanced search and the resource tabs), so each widget gets column selection,
 * client-side sorting, pagination, search and filtering for free — instead of a
 * bespoke static list.
 */
export const RecordListWidget = ({
    config,
    widget,
}: WidgetComponentProps<RecordListWidgetConfig>) => {
    const history = useHistory();
    // Container git-write-lock state (keyed by container id), from RouterContext —
    // the same source ResourcesSingle's editor gates on. Non-CO resource-class rows
    // in a git-write-locked bundle/project must not be inline-edited.
    const gitLockData = useContext(RouterContext)?.gitLockData;
    const [records, setRecords] = useState<ListedRecord[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setLoading] = useState(true);
    const isMounted = useRef(true);
    // The last rows committed to state, used to skip a no-op re-render when a
    // `live` re-poll returns byte-for-byte the same data (see fetchRecords).
    const prevDataRef = useRef<ListedRecord[] | undefined>(undefined);
    // Cheap change-signature of the last committed rows (id + modified/created_at +
    // provisioning_state). A `live` re-poll first fetches ONLY these signature
    // fields (a tiny payload) and, when the signature is unchanged — the common
    // case for an idle dashboard — skips the full heavy fetch entirely, so no big
    // payload is fetched, JSON-parsed or deep-diffed. That per-response processing
    // of the full list (up to 250 rows with nested identity/project/value fields)
    // was the residual steady-state CPU burn after the earlier polling fixes.
    const prevSignatureRef = useRef<string | undefined>(undefined);
    // The fetch shape (query fields + variables) the cached signature belongs to.
    // A signature probe is only valid for the SAME shape: when the shape changes
    // (e.g. the attribute schema loads and `value` joins the selection) the next
    // fetch must be a full heavy fetch. Checked inside fetchRecords so it does not
    // depend on effect order (the live-refresh hook's fetch runs before the reset
    // effect below when fetchRecords changes).
    const prevSignatureShapeRef = useRef<string | undefined>(undefined);
    const fitContainerRef = useRef<HTMLDivElement | null>(null);

    const recordType = config?.record_type;
    const apiType = typeof recordType === "string" ? recordType.toLowerCase() : "";
    const isCustomObject = apiType === "custom_object";
    // Only resource-class types carry the location/identity relations; activity
    // and relationship types keep the lean name + created_at selection.
    const isResourceClass = apiType
        ? getRecordClass(apiType as FeConfigType) === "resource"
        : false;
    // Option (A) — record_list ALWAYS uses height-adaptive server-side pagination
    // (Margot + Stefan, 2026-09-16/2026-09-22, operator-task-fe-list-widgets-
    // serverside-lazy-pagination-standard). The PAGE SIZE is derived from the space
    // actually available (capped at MAX_ADAPTIVE_ROWS), the true total row count is
    // shown, and switching pages fetches only that page (progressive per-page
    // loading) — so the dashboard holds one visible page in memory instead of a
    // whole `limit`-sized batch (Margot's memory concern). Widget HEIGHT is the
    // single source of the page size; `config.limit` is not a knob for record_list
    // (kept in the schema for back-compat with stored configs, but ignored here).
    // The classic fixed-count / client-side-pagination branches below are the
    // transitional fallback pending their removal + an existing-dashboard config
    // sweep (operator-task-fe-record-list-deprecate-classic-limit-path).
    const heightAdaptive = true;
    // Adaptive PAGE SIZE: how many whole rows fit the panel height, derived from a
    // row-height CONSTANT (not a data-dependent measurement). Because it is
    // data-independent, the widget fetches exactly one page of `pageSize` rows in a
    // single request per page view — no over-fetch, no measure→refetch settle loop.
    // `null` until the panel has been measured → fetchRecords defers.
    const [pageSize, setPageSize] = useState<number | null>(null);
    // Current page start (adaptive server-side pagination). Reset to 0 when the
    // page size, sort, or the widget's data selection changes.
    const [offset, setOffset] = useState(0);
    // Server total row count (adaptive) — drives the paginator's page count.
    const [totalCount, setTotalCount] = useState(0);
    // Live server-side sort override (adaptive): set when the user sorts a column
    // (RecordTable calls updateQuery({order}) in server-side mode). Takes
    // precedence over the persisted/config order for the fetch.
    const [orderOverride, setOrderOverride] = useState<string | null>(null);
    // Effective FETCH row count = the height-derived page size. `null` until the
    // panel has been measured → fetchRecords defers (see the measure gate below).
    const limit = pageSize;
    const live = config?.live ?? false;
    const tableSettingsKey = `dashboard-widget-${widget.uid}`;
    // Row density: the widget config's `density` is the default; the user's pick
    // in the table toolbar (persisted per widget) overrides it. RecordTable
    // reports the effective density back so the page size re-fits on a change.
    const configDensity =
        normalizeTableDensity(config?.density) ?? DEFAULT_TABLE_DENSITY;
    const [density, setDensity] = useState<TableDensity>(() =>
        getInitialDensity(tableSettingsKey, configDensity),
    );
    const rowHeightPx = adaptiveRowHeightForDensity(density);
    // Default display order (also the sort indicator's default). The classic-mode
    // fetch has always used `config.order` directly (null → the backend default);
    // adaptive mode is server-paginated, so its fetch order must MATCH the sort
    // indicator to avoid a reload desync — fold in the persisted widget sort and
    // the live sort override, falling back to config.order then this default.
    const configOrder = config?.order ?? null;
    const displayDefaultOrder = isResourceClass ? "-modified_at" : "-created_at";
    // Persisted per-widget sort (survives reload; written by RecordTable's
    // onColumnSortChange). Read once on mount — live sorts flow via orderOverride.
    const persistedWidgetOrder = useMemo<string | null>(() => {
        const stored = getTableSettings(tableSettingsKey, "order");
        return typeof stored === "string" && stored.length >= 2 ? stored : null;
    }, [tableSettingsKey]);
    const adaptiveOrder =
        orderOverride ?? persistedWidgetOrder ?? configOrder ?? displayDefaultOrder;
    // The order actually sent to the server: effective adaptive order, or the
    // classic config order (unchanged from before).
    const order = heightAdaptive ? adaptiveOrder : configOrder;
    // Item 2 — CO object_template ATTRIBUTE columns. When set (custom_object
    // only), render this template's value.<attr> fields as extra columns. Pure
    // display hint, decoupled from `filter`.
    const objectTemplateId =
        isCustomObject && typeof config?.object_template_id === "string"
            ? config.object_template_id
            : null;
    // The schema of the attribute-column object template (fetched once); its
    // `properties`/`hidden` drive getSchemaFormColumns.
    const [attributeSchema, setAttributeSchema] = useState<Record<string, any> | null>(null);
    const filter =
        config?.filter == null
            ? null
            : typeof config.filter === "string"
            ? config.filter
            : JSON.stringify(config.filter);
    // The filter actually sent: ordering by a `value.<attr>` column needs an
    // object_template_id condition, which the backend requires for value.* order.
    const fetchFilter = useMemo(
        () => withObjectTemplateFilter(filter, objectTemplateId, order),
        [filter, objectTemplateId, order],
    );
    // Configurable default columns: an explicit ordered list of column keys the
    // widget author chose. Serialized so the columns memo has a stable primitive
    // dependency (the raw array identity from `config` may change per render).
    const columnsKey = JSON.stringify(
        Array.isArray(config?.columns) ? config.columns : null,
    );

    // The attribute keys the widget actually renders as columns. The SAME set is
    // used to (a) project the fetched CO `value` via the GraphQL `value_keys`
    // argument and (b) gate which attribute columns are built (getSchemaFormColumns
    // `includeKeys`). Keeping the two in lock-step means a big attribute no column
    // shows (e.g. an operator-task `body`/`result` markdown blob) is never fetched,
    // JSON-parsed or deep-diffed on every live poll — the measured dominant cost of
    // the operator dashboard — AND there is no built attribute column whose value
    // was not fetched, so toggling a column visible can never render an empty cell.
    // When the widget has an explicit `columns` list, this is the attribute keys
    // among it (configured order); otherwise the default-shown attribute set (the
    // first ≤7 non-hidden properties, mirroring getSchemaFormColumns' shown split).
    // `null` until the attribute schema is loaded → no projection yet (full value).
    const neededAttrKeys = useMemo<string[] | null>(() => {
        if (!attributeSchema?.properties) {
            return null;
        }
        const allAttrKeys = Object.keys(attributeSchema.properties);
        const attrKeySet = new Set(allAttrKeys);
        const parsed = JSON.parse(columnsKey) as unknown;
        const configured = Array.isArray(parsed)
            ? parsed.filter(
                  (k): k is string => typeof k === "string" && attrKeySet.has(k),
              )
            : null;
        if (configured && configured.length) {
            return configured;
        }
        // No explicit columns: mirror getSchemaFormColumns' default shown split —
        // the first ≤7 non-hidden properties are the shown columns; only those are
        // built (includeKeys) and therefore only those need fetching.
        const hidden = new Set<string>(attributeSchema.hidden ?? []);
        const shown: string[] = [];
        for (const key of allAttrKeys) {
            if (!hidden.has(key) && shown.length < 7) {
                shown.push(key);
            }
        }
        return shown;
    }, [attributeSchema, columnsKey]);

    const fetchRecords = useCallback(async (): Promise<
        boolean | { changed: boolean; ok: boolean }
    > => {
        if (!apiType) {
            if (isMounted.current) {
                setError("Widget config is missing a record_type.");
                setLoading(false);
            }
            return false;
        }
        // Height-adaptive mode: defer the request until the panel has been measured
        // (pageSize set → limit non-null). This makes the very first fetch already
        // sized to the panel — one fetch — instead of a DEFAULT_LIMIT bootstrap
        // fetch followed by measurement-driven refetches.
        if (heightAdaptive && limit == null) {
            return false;
        }
        // Signature-gate for `live` re-polls: after the first successful heavy
        // load, fetch ONLY the change-signature fields (id + modified/created_at +
        // provisioning_state) and, when the signature matches the last committed
        // rows, return early WITHOUT the full heavy fetch. An idle dashboard's
        // widgets poll continuously but their data rarely changes, so this replaces
        // a per-poll fetch+parse+deep-diff of the full (up to 250-row, deeply
        // nested) payload with a tiny id/timestamp fetch in the common case. Only
        // when the signature actually changes do we pay for the heavy fetch below.
        // Skipped on the first load (no signature yet) and for non-live widgets
        // (which fetch once, so the extra round-trip would be pure overhead).
        //
        // The probe uses the bare `_plain_list` in BOTH modes (it needs no total
        // count) but, in adaptive mode, includes `$offset` so it probes the CURRENT
        // page's rows — the same rows the heavy fetch would return.
        const fetchShape = recordListFetchShape([
            apiType,
            objectTemplateId,
            heightAdaptive,
            limit,
            offset,
            order,
            fetchFilter,
            neededAttrKeys,
        ]);
        if (
            canUseSignatureProbe({
                live,
                signature: prevSignatureRef.current,
                signatureShape: prevSignatureShapeRef.current,
                fetchShape,
            })
        ) {
            const sigResourceField = isResourceClass ? "modified_at" : "created_at";
            const sigCoField = isCustomObject ? "provisioning_state" : "";
            const sigListField = `${apiType}_plain_list`;
            const sigArgs = heightAdaptive
                ? "(limit: $limit, order: $order, filter: $filter, offset: $offset)"
                : "(limit: $limit, order: $order, filter: $filter)";
            const sigQuery = `query DashboardRecordListSig($limit: Int, $order: String, $filter: String${
                heightAdaptive ? ", $offset: Int" : ""
            }) {
                ${sigListField}${sigArgs} {
                    id
                    ${sigResourceField}
                    ${sigCoField}
                }
            }`;
            try {
                const sigResponse = await graphqlPost({
                    query: sigQuery,
                    variables: heightAdaptive
                        ? { limit, order, filter: fetchFilter, offset }
                        : { limit, order, filter: fetchFilter },
                });
                const sigRows = sigResponse?.data?.[sigListField];
                const signature = computeRowsSignature(
                    Array.isArray(sigRows) ? sigRows : [],
                );
                if (signature === prevSignatureRef.current) {
                    // Unchanged: clear any stale transient error and stop the
                    // spinner, but do not re-render the list or bump the timestamp.
                    if (isMounted.current) {
                        setError(null);
                        setLoading(false);
                    }
                    return false;
                }
                // Changed → fall through to the heavy fetch (it recomputes and
                // stores the signature from the full rows).
            } catch (err: any) {
                // A failed signature probe must not silently freeze the widget —
                // fall through to the heavy fetch, which surfaces the error the
                // same way it always has.
            }
        }
        // Custom objects additionally carry provisioning_state + their object_template.
        // When attribute columns are requested (item 2) also fetch the CO `value`
        // blob — getSchemaFormColumns' dtos read value.<attr> from each row — plus
        // the writability fields the inline-edit gating reads
        // (getAttributeEditMode: a row is editable only when it is not read-only
        // and not in trash). Without these, every attribute cell would be treated
        // as writable (undefined → falsy → "not read-only") and a read-only/locked
        // CO's cells would wrongly become editable. Mirrors the fields the
        // object_template CO list already selects (is_readonly, deleted_at) and
        // adds the project/bundle read-only flags the gating also honours.
        // `description` + the writability-gating fields (is_readonly /
        // is_project_readonly / is_bundle_readonly / deleted_at) are fetched for
        // EVERY custom object, not only when attribute columns are requested,
        // because scope (ii) slice 3 enables inline-edit of the top-level
        // name/description fields on this widget (custom objects) — the editors
        // seed from `description` and isRecordFieldEditable gates on the
        // read-only flags. The `value` attribute blob is still only needed for
        // the attribute columns, so it stays gated on objectTemplateId.
        const customObjectFields = isCustomObject
            ? `provisioning_state
               object_template_id
               object_template_id_object_template { id name }
               description
               is_readonly
               is_project_readonly
               is_bundle_readonly
               deleted_at
               ${objectTemplateId && neededAttrKeys && neededAttrKeys.length ? "value" : ""}`
            : "";
        // Non-custom-object resource-class types get the top-level `description`
        // plus the writability-gating fields the inline name/description editors
        // read: is_readonly / is_project_readonly / is_bundle_readonly / deleted_at
        // (isRecordFieldEditable) and the container-id scalars (project_id /
        // bundle_id / workspace_id) the git-lock gate resolves
        // (isRecordFieldEditableWithGitLock). Custom objects already fetch these via
        // customObjectFields; activity/relationship types are not editable here.
        const nonCoEditFields =
            isResourceClass && !isCustomObject
                ? `description
                   is_readonly
                   is_project_readonly
                   is_bundle_readonly
                   deleted_at
                   project_id
                   bundle_id
                   workspace_id`
                : "";
        // Dashboards carry a user-chosen `icon` (curated `mdi:<key>` or uploaded
        // `data:` URI) on the row itself; select it so the Name column can render
        // it (see the name dto's `image` passthrough). Gated to `dashboard` — the
        // only listable type here whose own row has an `icon` column that is not
        // already resolved via a redux slice (`imageProps`); adding `icon` to a
        // `_plain_list` of a type without the column would error the whole query.
        const iconField = apiType === "dashboard" ? "icon" : "";
        // Resource-class types get the shared default-column fields; other classes
        // (activity/relationship) keep the lean name + created_at selection.
        const bodyFields = isResourceClass
            ? `${RESOURCE_DEFAULT_FIELDS}
               ${customObjectFields}
               ${nonCoEditFields}
               ${iconField}`
            : `created_at
               ${iconField}`;
        // Read-side value projection: when the widget renders CO attribute columns,
        // fetch ONLY those attribute keys of `value` (GraphQL `value_keys`) instead
        // of the whole blob. `value` is only selected when there are keys to project
        // (see customObjectFields), so the two conditions match.
        const useValueProjection =
            isCustomObject &&
            !!objectTemplateId &&
            Array.isArray(neededAttrKeys) &&
            neededAttrKeys.length > 0;
        // Adaptive mode fetches ONE page with real server-side pagination via the
        // `<type>_list` wrapper (`{ <type>_count, <type>_list }`, accepts `offset`),
        // so a paginator can page through the whole set one page per request.
        // Classic mode keeps the bare `<type>_plain_list` (no offset, no count —
        // client-side pagination over the single limited fetch), UNCHANGED so the
        // many classic widgets cannot regress. The heavy field selection is
        // identical inside either shape; only the outer field name, the `$offset`
        // arg, and the `{count list}` wrapper differ.
        const heavyListField = heightAdaptive
            ? `${apiType}_list`
            : `${apiType}_plain_list`;
        const offsetArg = heightAdaptive ? ", offset: $offset" : "";
        const valueKeysArg = useValueProjection ? ", value_keys: $value_keys" : "";
        const listArgs = `(limit: $limit, order: $order, filter: $filter${offsetArg}${valueKeysArg})`;
        const innerSelection = `id
                name
                ${bodyFields}`;
        const query = `query DashboardRecordList($limit: Int, $order: String, $filter: String${
            heightAdaptive ? ", $offset: Int" : ""
        }${useValueProjection ? ", $value_keys: [String!]" : ""}) {
            ${heightAdaptive
                ? `${heavyListField}${listArgs} {
                ${apiType}_count
                ${apiType}_list {
                    ${innerSelection}
                }
            }`
                : `${heavyListField}${listArgs} {
                ${innerSelection}
            }`}
        }`;
        try {
            const baseVars: Record<string, any> = { limit, order, filter: fetchFilter };
            if (heightAdaptive) {
                baseVars.offset = offset;
            }
            if (useValueProjection) {
                baseVars.value_keys = neededAttrKeys;
            }
            const response = await graphqlPost({ query, variables: baseVars });
            const { rows: next, totalCount: nextCount } = parseRecordListResponse(
                response?.data,
                apiType,
                heightAdaptive,
            );
            // If the widget unmounted while this poll was in flight (e.g. the user
            // navigated away from the dashboard), skip the expensive isEqual diff
            // over the payload entirely — nothing will be rendered, and running the
            // diff for every in-flight poll response after unmount is what keeps the
            // CPU burning (and the UI slow to recover) for a moment after leaving.
            if (!isMounted.current) {
                return false;
            }
            // Only re-render when the fetched rows actually differ from what is
            // already displayed. A `live` widget re-polls every POLL_INTERVAL_MS;
            // without this guard each poll replaces `records` with a fresh array
            // of identical content, forcing a full re-render of the (heavy)
            // RecordTable and burning browser CPU while nothing changed. The
            // returned "changed" flag also lets useDashboardLiveRefresh skip
            // bumping `lastUpdatedAt` on an unchanged poll, so an idle widget does
            // not re-render at all.
            const changed = !isEqual(prevDataRef.current, next);
            if (isMounted.current) {
                if (changed) {
                    prevDataRef.current = next;
                    setRecords(next);
                }
                // Adaptive: keep the server total current for the paginator (it can
                // change even when the current page's rows do not — e.g. a record
                // added on a later page).
                if (nextCount != null) {
                    setTotalCount(nextCount);
                }
                // Always refresh the cached signature from the full rows — even on
                // an unchanged heavy fetch (e.g. a no-op save that bumped
                // modified_at without changing displayed content) — so the next
                // live poll's signature probe compares against current state and
                // does not re-trigger the heavy fetch every interval.
                prevSignatureRef.current = computeRowsSignature(next);
                prevSignatureShapeRef.current = fetchShape;
                setError(null);
                setLoading(false);
            }
            // Rich result: a successful fetch (even an unchanged one) lets a
            // manual refresh confirm "Updated just now"; `ok:false` below keeps
            // a failed refresh from stamping over stale/errored data.
            return { changed, ok: true };
        } catch (err: any) {
            if (isMounted.current) {
                setError(describeWidgetFetchError(err, "Failed to load records."));
                setLoading(false);
            }
            return { changed: false, ok: false };
        }
    }, [apiType, isResourceClass, isCustomObject, objectTemplateId, heightAdaptive, limit, offset, order, fetchFilter, live, neededAttrKeys]);

    // Fetch the attribute-column object template's schema once (item 2). The
    // widget's object_template_id is a config-level display hint, so resolve the
    // schema from the template itself rather than per-row (rows may belong to
    // other templates when filter/display are decoupled).
    useEffect(() => {
        if (!objectTemplateId) {
            setAttributeSchema(null);
            return;
        }
        let cancelled = false;
        const query = `query DashboardWidgetOtSchema($filter: String) {
            object_template_plain_list(filter: $filter) { id schema }
        }`;
        const otFilter = JSON.stringify({
            and: [{ field: "id", op: "eq", value: objectTemplateId }],
        });
        graphqlPost({ query, variables: { filter: otFilter } })
            .then((response) => {
                if (cancelled) {
                    return;
                }
                const ot = response?.data?.object_template_plain_list?.[0];
                setAttributeSchema(ot?.schema ?? null);
            })
            .catch(() => {
                if (!cancelled) {
                    setAttributeSchema(null);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [objectTemplateId]);

    const { lastUpdatedAt, refresh, isRefreshing } = useDashboardLiveRefresh({
        live,
        pollIntervalMs: POLL_INTERVAL_MS,
        fetch: fetchRecords,
    });

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        setLoading(true);
        // The fetch shape (record type / filter / order / limit) changed: invalidate
        // the cached change-signature so the next fetch is a full heavy fetch rather
        // than a signature probe against rows from the previous configuration.
        prevSignatureRef.current = undefined;
    }, [fetchRecords]);

    // Compute the panel's available height (below the toolbar + column header) from
    // ONLY data-independent inputs, so it never depends on how many rows are
    // rendered — the coupling that created the old measure→refetch settle loop:
    //  - available height: the nearest BOUNDED scroll-clipping ancestor's
    //    clientHeight (measuredAvailableHeight) — NOT the `height: 100%` wrapper's
    //    own rect, which grows to its CONTENT height when the percentage chain fails
    //    to resolve and would drive a grow-taller runaway;
    //  - top chrome: the table header's bottom edge (toolbar + column header). The
    //    header renders even with zero rows, so this is measurable BEFORE any fetch.
    // Returns { availableHeight, containerTop, usable } or null when not yet
    // measurable.
    const measureGeometry = useCallback(() => {
        const container = fitContainerRef.current;
        if (!container) {
            return null;
        }
        const header = container.querySelector("thead") as HTMLElement | null;
        const firstRow = container.querySelector("tbody tr") as HTMLElement | null;
        const anchor = header ?? firstRow;
        if (!anchor) {
            return null;
        }
        const availableHeight = measuredAvailableHeight(container);
        const containerTop = container.getBoundingClientRect().top;
        const anchorRect = anchor.getBoundingClientRect();
        // Anchor on the header BOTTOM (chrome above rows) or, as a fallback, a row TOP.
        const topChrome = (header ? anchorRect.bottom : anchorRect.top) - containerTop;
        // Chrome BELOW the rows inside the same clipping box: the server-side
        // pagination footer (adaptive mode). Measured live (data-independent — the
        // footer renders whenever the table does), with a constant fallback before
        // it has painted. Subtracted from the usable height so the footer can never
        // push the last row of a page past the panel into an internal scrollbar.
        const paginator = container.querySelector(
            ".MuiTablePagination-root",
        ) as HTMLElement | null;
        const bottomChrome =
            paginator && paginator.getBoundingClientRect().height > 0
                ? paginator.getBoundingClientRect().height
                : ADAPTIVE_PAGINATION_FOOTER_PX;
        return { availableHeight, containerTop, topChrome, bottomChrome };
    }, []);

    // Data-INDEPENDENT: derive the PAGE SIZE (rows per page) from the panel height
    // and the row-height constant. Because it never depends on the rendered rows,
    // it gates exactly ONE fetch of `pageSize` rows per page view — no over-fetch,
    // no measure→refetch settle loop. Mirrored in a ref so a size change can reset
    // the page offset in the SAME batched update (both are set inside a layout
    // effect or the debounced resize timeout, so React batches them into one
    // render → one refetch, never a stale-offset + page-0 double fetch).
    const pageSizeRef = useRef<number | null>(null);
    const measurePageSize = useCallback(() => {
        const geo = measureGeometry();
        if (!geo) {
            return;
        }
        const fit = computeAdaptiveRowCount(
            geo.availableHeight,
            geo.topChrome,
            rowHeightPx,
            MAX_ADAPTIVE_ROWS,
            geo.bottomChrome,
        );
        if (fit == null || pageSizeRef.current === fit) {
            return;
        }
        pageSizeRef.current = fit;
        setPageSize(fit);
        // A changed page size re-pages from the top. (No-op on the first
        // null→N measurement, where offset is already 0.)
        setOffset(0);
    }, [measureGeometry, rowHeightPx]);

    // Server-side pagination bridge for the RecordTable footer (adaptive mode):
    // paging updates the offset (→ one fetch of the new page), sorting updates the
    // order and returns to page 0. The rows-per-page control is hidden in adaptive
    // mode (page size is height-derived), so a `limit` change is intentionally
    // ignored here.
    const handleUpdateQuery = useCallback((change: any) => {
        if (!change) {
            return;
        }
        if (change.offset != null) {
            setOffset(change.offset);
        }
        if (change.order != null) {
            setOrderOverride(change.order);
            setOffset(0);
        }
    }, []);

    // Re-derive the page size when the widget resizes (debounced): a genuine
    // geometry change legitimately fits a different number of rows. A changed page
    // size pages back to the top (offset reset below), so at most one refetch
    // follows a resize.
    useEffect(() => {
        if (!heightAdaptive) {
            return;
        }
        const container = fitContainerRef.current;
        if (!container || typeof ResizeObserver === "undefined") {
            return;
        }
        let timer: ReturnType<typeof setTimeout> | null = null;
        const observer = new ResizeObserver(() => {
            if (timer) {
                clearTimeout(timer);
            }
            timer = setTimeout(() => {
                measurePageSize();
            }, RESIZE_REFIT_DEBOUNCE_MS);
        });
        observer.observe(container);
        return () => {
            if (timer) {
                clearTimeout(timer);
            }
            observer.disconnect();
        };
    }, [heightAdaptive, measurePageSize]);

    // Measure the page size once the empty table chrome paints. A layout effect
    // runs before paint, so the gated first fetch fires immediately at the right
    // size. Deliberately NOT keyed on `records`.
    useLayoutEffect(() => {
        if (heightAdaptive) {
            measurePageSize();
        }
    }, [heightAdaptive, measurePageSize]);

    // The widget's data selection (record type / filter / configured order)
    // changed → page back to the top and drop any live sort override.
    useEffect(() => {
        setOffset(0);
        setOrderOverride(null);
    }, [apiType, filter, configOrder]);

    // Clamp the offset back into range when the server total shrinks below the
    // current page (e.g. records deleted while a later page was shown), so the
    // paginator never sits on an empty page.
    useEffect(() => {
        if (!heightAdaptive || pageSize == null) {
            return;
        }
        const clamped = clampPageOffset(offset, pageSize, totalCount);
        if (clamped !== offset) {
            setOffset(clamped);
        }
    }, [heightAdaptive, offset, pageSize, totalCount]);

    // Defensive fallback: if the panel can't be measured (e.g. rendered detached /
    // at zero height), still issue a fetch at the default page size so it never
    // hangs on "loading". The normal path sets pageSize via measurePageSize first.
    useEffect(() => {
        if (!heightAdaptive || pageSize != null) {
            return;
        }
        const timer = setTimeout(() => {
            setPageSize((prev) => {
                if (prev != null) {
                    return prev;
                }
                pageSizeRef.current = DEFAULT_LIMIT;
                return DEFAULT_LIMIT;
            });
        }, 1200);
        return () => clearTimeout(timer);
    }, [heightAdaptive, pageSize]);

    const columns = useMemo<TableColumn[]>(() => {
        // The widget's own Name column keeps the correct in-widget link + the CO
        // provisioning dot (the shared name column relies on a resource_type field
        // that `_plain_list` does not return).
        const nameColumn: TableColumn = {
            label: "Name",
            key: "name",
            shown: true,
            sortable: true,
            dto: (record: ListedRecord, refresh?: () => void) => {
                const link = recordType ? (
                    <DashboardRecordLink
                        id={record.id}
                        name={record.name}
                        recordType={recordType}
                        provisioningState={record.provisioning_state}
                        image={record.icon}
                        commentCount={record.comment_count}
                        objectTemplateId={record.object_template_id}
                    />
                ) : (
                    record.name
                );
                // Custom-object rows get an inline name editor (same pencil +
                // popover as the object_template CO list), gated on the plain
                // read-only/trash writability check.
                if (isCustomObject) {
                    return (
                        <div className="inlineEditPopoverCell">
                            <div className="inlineEditPopoverCell__preview">{link}</div>
                            {isRecordFieldEditable(record) && (
                                <RecordFieldInlineEditPencil
                                    record={record}
                                    field="name"
                                    value={record.name}
                                    resourceName="custom_object"
                                    refresh={refresh}
                                />
                            )}
                        </div>
                    );
                }
                // Other resource-class types (flows, connectors, settings, …) also
                // get the inline name editor, but with the git-lock-aware gate: a
                // read-only/trashed row, or a row whose bundle/project holds a git
                // write lock, keeps the read-only navigation link only. The name
                // link stays the navigation affordance; the separate pencil edits.
                if (isResourceClass) {
                    return (
                        <div className="inlineEditPopoverCell">
                            <div className="inlineEditPopoverCell__preview">{link}</div>
                            {isRecordFieldEditableWithGitLock(record, gitLockData) && (
                                <RecordFieldInlineEditPencil
                                    record={record}
                                    field="name"
                                    value={record.name}
                                    resourceName={apiType}
                                    refresh={refresh}
                                />
                            )}
                        </div>
                    );
                }
                // Activity / relationship types have no inline-writability model —
                // read-only link.
                return link;
            },
        };

        const createdAtColumn: TableColumn = {
            label: "Created at",
            key: "created_at",
            shown: !isResourceClass,
            sortable: true,
            dto: (record: ListedRecord) => createdAtDto(record),
        };

        // Activity / relationship types: keep the lean Name + Created at set.
        if (!isResourceClass) {
            return [nameColumn, createdAtColumn];
        }

        // Resource-class types: render the same DEFAULT column set the advanced
        // search / resource lists use (Name, Location, Modified at/by, Created
        // at/by). Reuse the shared column dtos so styling stays consistent. No
        // context-menu (`setNewValue`) in a read-only widget.
        const defaultColumns: TableColumn[] = [
            nameColumn,
            locationColumn(undefined) as TableColumn,
            {
                label: "Modified at",
                key: "modified_at",
                shown: true,
                sortable: true,
                dto: (record: ListedRecord) => modifiedAtDto(record),
            },
            {
                label: "Modified by",
                key: "modified_by",
                shown: true,
                sortable: true,
                dto: (record: ListedRecord) => modifiedByDto(record),
            },
            createdAtColumn,
            {
                label: "Created by",
                key: "created_by",
                shown: false,
                sortable: true,
                dto: (record: ListedRecord) => createdByDto(record),
            },
        ];

        // Custom objects also get their "Object template" column (same definition
        // as the object_template resource list / advanced search).
        if (isCustomObject) {
            const coConfig = getRecordConfig("custom_object" as FeConfigType) as {
                additionalColumns?: (setNewValue?: any) => TableColumn[];
            };
            const coAdditional = coConfig?.additionalColumns?.(undefined);
            if (Array.isArray(coAdditional)) {
                defaultColumns.push(...coAdditional);
            }

            // Scope (ii) slice 3: an ADDABLE (shown:false, so the default column
            // set is unchanged) editable Description column — same pop-out markdown
            // editor as the object_template CO list. `description` is a real
            // top-level custom_object column (fetched above for every CO).
            defaultColumns.push({
                label: "Description",
                key: "description",
                shown: false,
                sortable: true,
                dto: (record: ListedRecord, refresh?: () => void) => (
                    <RecordDescriptionInlineEdit
                        record={record}
                        value={record?.description}
                        resourceName="custom_object"
                        refresh={refresh}
                    />
                ),
            });

            // Item 2: append the configured object template's attribute columns
            // (value.<attr>), reusing the exact same builder the object_template
            // resource screen's custom-object list uses, so styling matches.
            // `editable: true` turns on inline attribute-cell editing here too
            // (operator-task-fe-inline-edit-list-views scope (i): "prove the
            // pattern on the object_template CO list + dashboard record_list").
            // The same datatype-aware editors (boolean/dropdown immediate,
            // string/integer click-to-edit, text/json/markdown popover) apply;
            // the fetch above now selects the writability-gating fields so
            // read-only rows stay non-editable, and the widget threads `refresh`
            // into its RecordTable so a saved edit reloads the list.
            if (attributeSchema) {
                defaultColumns.push(
                    ...getSchemaFormColumns(attributeSchema, {
                        editable: true,
                        // Attribute columns sort server-side: keyed `value.<attr>`,
                        // the order token the backend list accepts. Configured
                        // `columns` may still name them by the bare attribute
                        // (findConfiguredColumn maps both).
                        sortable: true,
                        // Build attribute columns ONLY for the keys the widget
                        // fetches (value_keys projection) so no built column reads
                        // an un-fetched value → no empty cells on column toggle.
                        includeKeys: neededAttrKeys,
                    }),
                );
            }
        } else {
            // Non-CO resource-class types get the same ADDABLE (shown:false, so the
            // default column set is unchanged) editable Description column, with the
            // git-lock-aware writability gate passed explicitly: a git-write-locked
            // or read-only row shows only the read-only preview. `description` is a
            // real top-level column on every resource-class type (fetched above via
            // nonCoEditFields).
            defaultColumns.push({
                label: "Description",
                key: "description",
                shown: false,
                sortable: true,
                dto: (record: ListedRecord, refresh?: () => void) => (
                    <RecordDescriptionInlineEdit
                        record={record}
                        value={record?.description}
                        resourceName={apiType}
                        refresh={refresh}
                        editable={isRecordFieldEditableWithGitLock(record, gitLockData)}
                    />
                ),
            });
        }

        // Part 2 (operator-task-recordtable-reset-columns-and-widget-configurable-columns):
        // an explicit per-widget `columns` list overrides which columns show by
        // default AND their order. Columns named in the list are shown, in the
        // given order; every other column stays available in the column selector
        // but hidden by default. When unset, the automatic per-record_type default
        // set (above) is returned unchanged.
        const parsedColumns = JSON.parse(columnsKey) as unknown;
        const configuredColumns = Array.isArray(parsedColumns)
            ? parsedColumns.filter(
                  (k): k is string => typeof k === "string" && k.length > 0,
              )
            : null;
        if (configuredColumns && configuredColumns.length) {
            const byKey = new Map(defaultColumns.map((col) => [col.key, col]));
            const usedKeys = new Set<string>();
            const ordered: TableColumn[] = [];
            for (const key of configuredColumns) {
                const col = findConfiguredColumn(byKey, key);
                if (col && !usedKeys.has(col.key)) {
                    ordered.push({ ...col, shown: true });
                    usedKeys.add(col.key);
                }
            }
            // Keep the unconfigured columns reachable via the column selector,
            // hidden by default, in their original order.
            for (const col of defaultColumns) {
                if (!usedKeys.has(col.key)) {
                    ordered.push({ ...col, shown: false });
                }
            }
            return ordered;
        }

        return defaultColumns;
    }, [
        recordType,
        apiType,
        isResourceClass,
        isCustomObject,
        attributeSchema,
        columnsKey,
        neededAttrKeys,
        gitLockData,
    ]);

    // Deep-link the widget's rows into Advanced search with the SAME filter
    // pre-applied (reuses the shared helper that also backs the stat_tile widget
    // and the object_template CO list "Advanced search" button).
    const openAdvancedSearch = useCallback(() => {
        if (!recordType) {
            return;
        }
        history.push(buildRecordListAdvancedSearchPath(recordType, config?.filter));
    }, [config?.filter, history, recordType]);

    // Adaptive mode uses REAL server-side pagination: each fetch returns exactly the
    // current page of `pageSize` rows. Clamp the rendered rows to the current
    // `pageSize` as a display-safety invariant: the paginator advertises
    // rowsPerPage = pageSize (initialQuery.limit / rowsPerPageOptions below), so the
    // rendered rows must never exceed it. In steady state the heavy fetch already
    // returns exactly `pageSize` rows, so the slice is a no-op; it only trims a
    // transient/stale over-fetch (e.g. rows from a briefly-larger pre-settle
    // pageSize whose limit=pageSize refetch has not yet replaced them), which would
    // otherwise render an extra row under a smaller-page paginator and push the last
    // row + footer past the panel into an internal scrollbar. Classic mode renders
    // its single limited fetch with client-side pagination, unchanged.
    const displayData = clampAdaptiveDisplayRows(records, heightAdaptive, pageSize);

    const table = (
        <RecordTable
            data={displayData}
            totalItemCount={heightAdaptive ? totalCount : displayData.length}
            columns={columns}
            recordTypes={null}
            isLoading={isLoading}
            refresh={refresh}
            // Adaptive: supplying updateQuery flips RecordTable to server-side mode,
            // so paging/sorting are delegated back here (one fetch per page).
            // Classic: omitted → unchanged client-side pagination.
            updateQuery={heightAdaptive ? handleUpdateQuery : undefined}
            initialQuery={{
                // Adaptive: page size = height-derived rows-per-page (fall back to
                // the default while still measuring) + the current page offset.
                // Classic: fixed 10.
                limit: heightAdaptive ? pageSize ?? DEFAULT_LIMIT : 10,
                offset: heightAdaptive ? offset : undefined,
                order: heightAdaptive ? adaptiveOrder : displayDefaultOrder,
            }}
            tableSettingsKey={tableSettingsKey}
            options={{
                // Read-only widget: no row selection and no Cloudomation bulk-action
                // toolbar, but keep the shared column selector. Sorting + pagination
                // come from RecordTable itself. The left toolbar holds a single
                // "Advanced search" deep-link that opens these rows (same filter) in
                // the full advanced-search screen.
                selectableRows: "none",
                toolbar: "custom",
                customLeftToolbar: (() =>
                    recordType ? (
                        <ShowAllButton
                            onClick={openAdvancedSearch}
                            title="Show all these records in the advanced search (same filter applied)"
                        />
                    ) : null) as any,
                density: configDensity,
                onDensityChange: setDensity,
                viewColumns: true,
                // Adaptive mode: real server-side pagination. Keep the paginator
                // (prev/next/page numbers) but HIDE the rows-per-page selector — the
                // page size is derived from the panel height (MUI TablePagination
                // hides the select when there is a single option). `currentOffset`
                // lets RecordTable snap back to page 0 when the query resets.
                // Classic mode keeps its default client-side pagination unchanged.
                ...(heightAdaptive
                    ? {
                          currentOffset: offset,
                          ...(pageSize != null
                              ? { rowsPerPageOptions: [pageSize] }
                              : {}),
                      }
                    : {}),
            }}
        />
    );

    // Keep the "Advanced search" deep-link reachable in the empty state — that is
    // exactly when the user wants to jump out to widen/inspect the filter
    // (operator-task-fe-record-list-widget-empty-show-advanced-search). The
    // RecordTable toolbar (which normally hosts the link) is not rendered when
    // there are no rows, so surface the link here instead.
    const emptyBlock = (
        <div className="DashboardWidget__empty">
            <div>No records.</div>
            {recordType ? (
                <ShowAllButton
                    onClick={openAdvancedSearch}
                    title="Show all these records in the advanced search (same filter applied)"
                />
            ) : null}
        </div>
    );

    let content;
    // Keep the last successfully-rendered rows on a failed re-fetch: only fall
    // back to the full error view when there is no prior data to show. A stale
    // re-fetch error is surfaced inline on the live bar instead (via `error`).
    if (error && records.length === 0) {
        content = <div className="DashboardWidget__error">{error}</div>;
    } else if (heightAdaptive) {
        // Adaptive mode: render the table (with its header chrome) even while
        // loading/empty so the panel can be measured and the single gated fetch
        // can fire. Show the custom empty-state block only once an empty result
        // has actually been LOADED (pageSize set, not loading) — not during the
        // pre-fetch measuring phase, when records are still empty by definition.
        content =
            !isLoading && records.length === 0 && pageSize != null ? (
                emptyBlock
            ) : (
                // Full-height measuring container so the panel geometry can be read.
                <div ref={fitContainerRef} style={{ height: "100%" }}>
                    {table}
                </div>
            );
    } else if (isLoading && records.length === 0) {
        content = <div className="DashboardWidget__empty">Loading…</div>;
    } else if (!isLoading && records.length === 0) {
        content = emptyBlock;
    } else {
        // Classic (explicit-limit) mode: render the table directly (zero layout change).
        content = table;
    }

    return (
        <DashboardLiveWidgetShell
            live={live}
            lastUpdatedAt={lastUpdatedAt}
            onRefresh={refresh}
            isRefreshing={isRefreshing}
            error={error}
        >
            {content}
        </DashboardLiveWidgetShell>
    );
};
